import { useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CellBadge } from './cell-badge'
import { createMemoCell } from '@/lib/create-memo-cell'
import {
  RelationshipEditor,
  useRelationshipEditor,
  type RelationshipItem,
} from './relationship-editor'
import {
  useIndustriesSearch,
  useSectorsSearch,
  useLocationsSearch,
} from '@/hooks/use-reference-data'
import {
  useAddCompanyIndustry,
  useRemoveCompanyIndustry,
  useAddCompanySector,
  useRemoveCompanySector,
  useUpdateCompany,
} from '@/hooks/use-companies'
import { formatLocationName } from '@/lib/formatters'
import { showSuccess, showError } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { useCanEditCompany } from '@/hooks/use-permissions'
import type { UpdateCompany } from '@/types/api'

type EditableFieldType = 'industries' | 'sectors' | 'location'

interface EditableCellProps {
  companyId: string
  primaryOwnerId?: string | null
  secondaryOwnerId?: string | null
  fieldType: EditableFieldType
  currentValues: RelationshipItem[]
  displayProperty?: string
}

function EditableCellBase({
  companyId,
  primaryOwnerId,
  secondaryOwnerId,
  fieldType,
  currentValues,
  displayProperty = 'name',
}: EditableCellProps) {
  const canEditCompany = useCanEditCompany()
  const canEdit = canEditCompany(primaryOwnerId, secondaryOwnerId)
  const { open, setOpen, searchTerm, setSearchTerm, debouncedSearch } =
    useRelationshipEditor()

  const isMulti = fieldType !== 'location'

  // Get the appropriate search hook based on field type
  const industriesSearch = useIndustriesSearch({
    search: debouncedSearch,
    enabled: open && fieldType === 'industries',
  })
  const sectorsSearch = useSectorsSearch({
    search: debouncedSearch,
    enabled: open && fieldType === 'sectors',
  })
  const locationsSearch = useLocationsSearch({
    search: debouncedSearch,
    enabled: open && fieldType === 'location',
  })

  // Get the appropriate mutation hooks
  const addIndustry = useAddCompanyIndustry()
  const removeIndustry = useRemoveCompanyIndustry()
  const addSector = useAddCompanySector()
  const removeSector = useRemoveCompanySector()
  const updateCompany = useUpdateCompany()
  const [optimisticValues, setOptimisticValues] = useState<
    RelationshipItem[] | undefined
  >(undefined)

  const currentValuesKey = useMemo(
    () => currentValues.map((value) => value.id).join('|'),
    [currentValues]
  )
  const [prevValuesKey, setPrevValuesKey] = useState(currentValuesKey)

  if (currentValuesKey !== prevValuesKey) {
    setPrevValuesKey(currentValuesKey)
    setOptimisticValues(undefined)
  }

  const displayValues = optimisticValues ?? currentValues
  const mutationIdRef = useRef(0)

  // Determine which search results and loading state to use
  let searchData: RelationshipItem[] | undefined
  let isSearchLoading = false
  let placeholder = 'Search...'

  switch (fieldType) {
    case 'industries':
      searchData = industriesSearch.data?.data
      isSearchLoading = industriesSearch.isLoading
      placeholder = 'Search industries...'
      break
    case 'sectors':
      searchData = sectorsSearch.data?.data
      isSearchLoading = sectorsSearch.isLoading
      placeholder = 'Search sectors...'
      break
    case 'location':
      // Transform location data to match RelationshipItem interface
      searchData = locationsSearch.data?.data?.map((loc) => ({
        id: loc.id,
        name: formatLocationName(loc),
      }))
      isSearchLoading = locationsSearch.isLoading
      placeholder = 'Search locations...'
      break
  }

  const handleAdd = (item: RelationshipItem) => {
    const mutId = ++mutationIdRef.current

    if (isMulti) {
      setOptimisticValues((prev) => {
        const base = prev ?? currentValues
        if (base.some((value) => value.id === item.id)) return base
        return [...base, item]
      })
    } else {
      setOptimisticValues([item])
    }

    switch (fieldType) {
      case 'industries':
        addIndustry.mutate(
          {
            companyId,
            industryId: item.id,
            name: item.name,
          },
          {
            onSuccess: () => showSuccess('Industry added'),
            onError: (error) => {
              if (mutationIdRef.current === mutId) {
                setOptimisticValues(undefined)
              }
              showError(error, 'Failed to add industry')
            },
          }
        )
        break
      case 'sectors':
        addSector.mutate(
          {
            companyId,
            sectorId: item.id,
            name: item.name,
          },
          {
            onSuccess: () => showSuccess('Sector added'),
            onError: (error) => {
              if (mutationIdRef.current === mutId) {
                setOptimisticValues(undefined)
              }
              showError(error, 'Failed to add sector')
            },
          }
        )
        break
      case 'location': {
        // Find the full location object for optimistic update
        const fullLocation = locationsSearch.data?.data?.find(
          (loc) => loc.id === item.id
        )
        updateCompany.mutate(
          {
            id: companyId,
            data: { locationId: item.id } as UpdateCompany,
            location: fullLocation
              ? {
                  id: fullLocation.id,
                  country: fullLocation.country,
                  state: fullLocation.state,
                  city: fullLocation.city,
                }
              : undefined,
            optimistic: false,
          },
          {
            onSuccess: () => showSuccess('Location updated'),
            onError: (error) => {
              if (mutationIdRef.current === mutId) {
                setOptimisticValues(undefined)
              }
              showError(error, 'Failed to update location')
            },
          }
        )
        break
      }
    }
  }

  const handleRemove = (id: string) => {
    const mutId = ++mutationIdRef.current

    setOptimisticValues((prev) => {
      const base = prev ?? currentValues
      return base.filter((value) => value.id !== id)
    })

    switch (fieldType) {
      case 'industries':
        removeIndustry.mutate(
          { companyId, industryId: id },
          {
            onSuccess: () => showSuccess('Industry removed'),
            onError: (error) => {
              if (mutationIdRef.current === mutId) {
                setOptimisticValues(undefined)
              }
              showError(error, 'Failed to remove industry')
            },
          }
        )
        break
      case 'sectors':
        removeSector.mutate(
          { companyId, sectorId: id },
          {
            onSuccess: () => showSuccess('Sector removed'),
            onError: (error) => {
              if (mutationIdRef.current === mutId) {
                setOptimisticValues(undefined)
              }
              showError(error, 'Failed to remove sector')
            },
          }
        )
        break
      case 'location':
        // For location, removing means setting to undefined
        updateCompany.mutate(
          {
            id: companyId,
            data: { locationId: undefined } as UpdateCompany,
            optimistic: false,
          },
          {
            onSuccess: () => showSuccess('Location removed'),
            onError: (error) => {
              if (mutationIdRef.current === mutId) {
                setOptimisticValues(undefined)
              }
              showError(error, 'Failed to remove location')
            },
          }
        )
        break
    }
  }

  const isEmpty = displayValues.length === 0

  // Use outline-plain for sectors/industries, solid for location
  const badgeVariant =
    fieldType === 'sectors' || fieldType === 'industries'
      ? 'outline-plain'
      : 'solid'

  if (!canEdit) {
    if (isEmpty) {
      return <span className="text-xs text-muted-foreground">—</span>
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {displayValues.map((value) => (
          <CellBadge
            key={value.id}
            value={
              displayProperty === 'name'
                ? value.name
                : ((value as unknown as Record<string, unknown>)[
                    displayProperty
                  ] as string | null)
            }
            variant={badgeVariant}
          />
        ))}
      </div>
    )
  }

  return (
    <RelationshipEditor
      currentValues={displayValues}
      isMulti={isMulti}
      onAdd={handleAdd}
      onRemove={handleRemove}
      searchData={searchData}
      isSearchLoading={isSearchLoading}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      placeholder={placeholder}
      open={open}
      onOpenChange={setOpen}
    >
      <Button
        variant="ghost"
        className={cn(
          'flex items-center gap-1 h-auto px-1 -mx-1',
          'hover:bg-muted/50'
        )}
      >
        {isEmpty ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Plus className="size-3" />
            Add
          </span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {displayValues.map((value) => (
              <CellBadge
                key={value.id}
                value={
                  displayProperty === 'name'
                    ? value.name
                    : ((value as unknown as Record<string, unknown>)[
                        displayProperty
                      ] as string | null)
                }
                variant={badgeVariant}
                showChevron
              />
            ))}
          </div>
        )}
      </Button>
    </RelationshipEditor>
  )
}

export const EditableCell = createMemoCell(EditableCellBase, 'EditableCell')
